import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { STATUS_LABEL, EXPERIENCE_VALUES } from "@/lib/media";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Wordmark } from "@/components/Brand";

const expLabel = (val) => EXPERIENCE_VALUES.find((e) => e.value === val)?.label || val;

const statusStyles = {
  new: "bg-cerenza-cherry text-cerenza-creamlight",
  contacted: "bg-cerenza-pink text-cerenza-cherry",
  confirmed: "bg-cerenza-ink text-cerenza-creamlight",
  archived: "bg-cerenza-pinklight text-cerenza-ink/60",
};

export default function AdminDashboard() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("cerenza_token");
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al cargar reservas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("cerenza_token")) {
      navigate("/admin/login", { replace: true });
      return;
    }
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem("cerenza_token");
    localStorage.removeItem("cerenza_email");
    navigate("/admin/login", { replace: true });
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Estado actualizado.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al actualizar.");
    }
  };

  const removeBooking = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success("Reserva eliminada.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al eliminar.");
    }
  };

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const counts = bookings.reduce(
    (acc, b) => ({ ...acc, [b.status]: (acc[b.status] || 0) + 1 }),
    { new: 0, contacted: 0, confirmed: 0, archived: 0 }
  );

  return (
    <main className="min-h-screen bg-cerenza-cream text-cerenza-ink" data-testid="admin-dashboard">
      <header className="px-6 md:px-12 py-5 border-b border-cerenza-pink/50 flex items-center justify-between sticky top-0 bg-cerenza-cream/90 backdrop-blur-xl z-10">
        <div className="flex items-center gap-8">
          <Link to="/" data-testid="admin-logo"><Wordmark className="h-9" /></Link>
          <span className="hidden md:block eyebrow">Panel del estudio</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden md:inline text-sm text-cerenza-ink/65">{localStorage.getItem("cerenza_email")}</span>
          <button data-testid="admin-logout" onClick={logout} className="btn-cherry-outline text-[11px] py-2.5 px-5">
            Cerrar sesión
          </button>
        </div>
      </header>

      <section className="px-6 md:px-12 py-12 md:py-16 max-w-[1440px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="eyebrow">— Reservas</p>
            <h1 className="font-display text-5xl md:text-6xl leading-[1.02] mt-3 text-cerenza-cherry">
              {bookings.length} solicitud{bookings.length === 1 ? "" : "es"}
            </h1>
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              { id: "all", label: `Todas · ${bookings.length}` },
              { id: "new", label: `Nuevas · ${counts.new}` },
              { id: "contacted", label: `Contactadas · ${counts.contacted}` },
              { id: "confirmed", label: `Confirmadas · ${counts.confirmed}` },
              { id: "archived", label: `Archivadas · ${counts.archived}` },
            ].map((f) => (
              <button
                key={f.id}
                data-testid={`filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-full uppercase text-[10px] tracking-[0.18em] font-semibold border transition-colors ${
                  filter === f.id
                    ? "bg-cerenza-cherry text-cerenza-creamlight border-cerenza-cherry"
                    : "border-cerenza-pink text-cerenza-ink/65 hover:border-cerenza-cherry hover:text-cerenza-cherry"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 bg-cerenza-creamlight rounded-3xl border border-cerenza-pink/60 overflow-hidden">
          <Table data-testid="bookings-table">
            <TableHeader>
              <TableRow className="border-cerenza-pink/40 hover:bg-transparent">
                <TableHead className="uppercase text-[10px] tracking-[0.18em] font-semibold text-cerenza-cherry">Cliente</TableHead>
                <TableHead className="uppercase text-[10px] tracking-[0.18em] font-semibold text-cerenza-cherry">Experiencia</TableHead>
                <TableHead className="uppercase text-[10px] tracking-[0.18em] font-semibold text-cerenza-cherry">Fecha</TableHead>
                <TableHead className="uppercase text-[10px] tracking-[0.18em] font-semibold text-cerenza-cherry">Contacto</TableHead>
                <TableHead className="uppercase text-[10px] tracking-[0.18em] font-semibold text-cerenza-cherry">Estado</TableHead>
                <TableHead className="uppercase text-[10px] tracking-[0.18em] font-semibold text-cerenza-cherry text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="py-16 text-center text-cerenza-ink/60">Cargando…</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-20 text-center" data-testid="empty-bookings">
                    <p className="font-display text-3xl text-cerenza-cherry">Sin reservas todavía.</p>
                    <p className="mt-3 text-sm text-cerenza-ink/60">Las nuevas solicitudes aparecerán aquí.</p>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((b) => (
                  <TableRow key={b.id} className="border-cerenza-pink/30" data-testid={`booking-row-${b.id}`}>
                    <TableCell className="py-5">
                      <p className="font-semibold text-cerenza-ink">{b.name}</p>
                      {b.message ? <p className="text-xs text-cerenza-ink/60 mt-1 max-w-xs truncate">{b.message}</p> : null}
                    </TableCell>
                    <TableCell className="text-sm">{expLabel(b.experience_type)}</TableCell>
                    <TableCell className="text-sm">{b.preferred_date}</TableCell>
                    <TableCell className="text-sm">
                      <div className="flex flex-col">
                        <a className="hover:text-cerenza-cherry" href={`mailto:${b.email}`}>{b.email}</a>
                        <a className="hover:text-cerenza-cherry text-cerenza-ink/60" href={`tel:${b.phone}`}>{b.phone}</a>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex px-3 py-1 rounded-full uppercase text-[10px] tracking-[0.18em] font-semibold ${statusStyles[b.status]}`}>
                        {STATUS_LABEL[b.status]}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`actions-${b.id}`} className="px-4 py-2 rounded-full border border-cerenza-pink text-xs uppercase tracking-[0.16em] font-semibold hover:border-cerenza-cherry hover:text-cerenza-cherry transition-colors">
                            Cambiar
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-cerenza-cream border-cerenza-pink rounded-2xl">
                          {["new", "contacted", "confirmed", "archived"].map((s) => (
                            <DropdownMenuItem key={s} onClick={() => updateStatus(b.id, s)} className="rounded-xl" data-testid={`set-${s}-${b.id}`}>
                              {STATUS_LABEL[s]}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem data-testid={`delete-${b.id}`} onClick={() => removeBooking(b.id)} className="text-cerenza-cherry rounded-xl">
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </main>
  );
}
